import { supabase } from "../lib/supabase";
import {
  MasterContainer,
  MasterContainerWithContainers,
} from "../types/booking";
import { masterTransportTypeMatches } from "../utils/masterContainerTypeUtils";

type MasterContainerWriteData = {
  reference?: string;
  eta_customer?: string | null;
  eta?: string | null;
  etd?: string | null;
  delayed_etd?: string | null;
  status?: string | null;
  track_and_trace?: string | null;
  transport_type?: number | null;
  agent_id?: number | null;
  origin_port?: number | null;
  destination_port?: number | null;
};

const MASTER_CONTAINER_SELECT = `
  *,
  agent_info:booking_agent(id, name, email),
  transport_type_info:booking_transport_types(id, name, abbreviation),
  origin_port_info:booking_origin_port(id, name, country),
  destination_port_info:booking_destination_port(id, name, country),
  master_containers_containers(
    container_id,
    containers(
      id,
      reference,
      status,
      etd,
      delayed_etd,
      eta,
      eta_customer,
      track_and_trace,
      transport_type,
      bookings(
        id,
        reference,
        status,
        transport_type,
        transport_type_info:booking_transport_types(id, name, abbreviation),
        company_info:company(id, name)
      )
    )
  )
`;

type MasterContainerRow = MasterContainer & {
  master_containers_containers?: Array<{
    container_id: number;
    containers: {
      id: number;
      reference: string | null;
      status: string | null;
      etd: string | null;
      delayed_etd?: string | null;
      eta: string | null;
      eta_customer?: string | null;
      track_and_trace: string | null;
      transport_type?: number | null;
      bookings?: Array<{
        id: number;
        reference: string | null;
        status: string | null;
        transport_type?: number | null;
        transport_type_info?:
          | { id: number; name: string; abbreviation: string }
          | { id: number; name: string; abbreviation: string }[]
          | null;
        company_info?:
          | { id: number; name: string }
          | { id: number; name: string }[]
          | null;
      }> | null;
    };
  }> | null;
};

const transformMasterContainers = (
  data: MasterContainerRow[] | null
): MasterContainerWithContainers[] =>
  (data?.map((masterContainer) => ({
    ...masterContainer,
    containers:
      masterContainer.master_containers_containers
        ?.map((mcc) => mcc.containers)
        .filter(Boolean) || [],
    container_count: masterContainer.master_containers_containers?.length || 0,
  })) || []) as MasterContainerWithContainers[];

/**
 * Fetch master containers with their associated containers.
 * Optionally scoped to a single agent (agent portal).
 */
export const fetchMasterContainers = async (options?: {
  agentId?: number;
}) => {
  try {
    let query = supabase
      .from("master_containers")
      .select(MASTER_CONTAINER_SELECT)
      .order("created_at", { ascending: false });

    if (options?.agentId != null) {
      query = query.eq("agent_id", options.agentId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return {
      data: transformMasterContainers(data as MasterContainerRow[]),
      error: null,
    };
  } catch (error) {
    console.error("Error fetching master containers:", error);
    return { data: [], error };
  }
};

/**
 * Create a new master container
 */
export const createMasterContainer = async (masterContainerData: {
  reference: string;
  eta_customer?: string | null;
  eta?: string | null;
  etd?: string | null;
  delayed_etd?: string | null;
  status?: string | null;
  track_and_trace?: string | null;
  transport_type?: number | null;
  agent_id?: number | null;
  origin_port?: number | null;
  destination_port?: number | null;
}) => {
  try {
    const { data, error } = await supabase
      .from("master_containers")
      .insert(masterContainerData)
      .select()
      .single();

    if (error) throw error;

    return { data: data as MasterContainer, error: null };
  } catch (error) {
    console.error("Error creating master container:", error);
    return { data: null, error };
  }
};

/**
 * Update a master container
 */
export const updateMasterContainer = async (
  id: number,
  masterContainerData: MasterContainerWriteData
) => {
  try {
    const { data, error } = await supabase
      .from("master_containers")
      .update(masterContainerData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    return { data: data as MasterContainer, error: null };
  } catch (error) {
    console.error("Error updating master container:", error);
    return { data: null, error };
  }
};

/**
 * Fetch master containers for filter dropdown (simplified)
 */
export const fetchMasterContainersForFilter = async (options?: {
  agentId?: number;
  transportTypeId?: number;
}) => {
  try {
    let query = supabase
      .from("master_containers")
      .select(
        "id, reference, transport_type, agent_id, created_at, transport_type_info:booking_transport_types(id, name, abbreviation)"
      )
      .order("created_at", { ascending: false });

    if (options?.agentId != null) {
      query = query.eq("agent_id", options.agentId);
    }
    if (options?.transportTypeId != null) {
      query = query.eq("transport_type", options.transportTypeId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: (data || []) as MasterContainer[], error: null };
  } catch (error) {
    console.error("Error fetching master containers for filter:", error);
    return { data: [] as MasterContainer[], error };
  }
};

/**
 * Bulk update containers in a master container with track_and_trace option
 */
export const bulkUpdateContainersInMaster = async (
  masterContainerId: number,
  updates: {
    eta_customer?: string | null;
    eta?: string | null;
    etd?: string | null;
    delayed_etd?: string | null;
    status?: string | null;
    track_and_trace?: string | null;
  }
) => {
  try {
    // First get all container IDs in this master container
    const { data: containerRelations, error: relationError } = await supabase
      .from("master_containers_containers")
      .select("container_id")
      .eq("master_container_id", masterContainerId);

    if (relationError) throw relationError;

    const containerIds = containerRelations?.map(rel => rel.container_id).filter(Boolean) || [];

    if (containerIds.length === 0) {
      return { success: true, updatedContainers: 0, error: null };
    }

    // Update all containers in this master container
    const { data, error } = await supabase
      .from("containers")
      .update(updates)
      .in("id", containerIds)
      .select("id, reference, status, eta, eta_customer, etd, delayed_etd, track_and_trace");

    if (error) throw error;

    return { success: true, updatedContainers: data?.length || 0, data, error: null };
  } catch (error) {
    console.error("Error bulk updating containers:", error);
    return { success: false, updatedContainers: 0, data: null, error };
  }
};

/**
 * Bulk update bookings in containers of a master container
 */
export const bulkUpdateBookingsInMaster = async (
  masterContainerId: number,
  updates: {
    status?: string | null;
  }
) => {
  try {
    // First get all container IDs in this master container
    const { data: containerRelations, error: relationError } = await supabase
      .from("master_containers_containers")
      .select("container_id")
      .eq("master_container_id", masterContainerId);

    if (relationError) throw relationError;

    const containerIds = containerRelations?.map(rel => rel.container_id).filter(Boolean) || [];

    if (containerIds.length === 0) {
      return { success: true, updatedBookings: 0, error: null };
    }

    // Update all bookings in these containers
    const { data, error } = await supabase
      .from("bookings")
      .update(updates)
      .in("container_id", containerIds)
      .select("id, reference, status, container_id");

    if (error) throw error;

    return { success: true, updatedBookings: data?.length || 0, data, error: null };
  } catch (error) {
    console.error("Error bulk updating bookings:", error);
    return { success: false, updatedBookings: 0, data: null, error };
  }
};

/**
 * Get preview of what will be updated in a master container
 */
export const getUpdatePreview = async (masterContainerId: number) => {
  try {
    // Get containers in master container
    const { data: containerData, error: containerError } = await supabase
      .from("master_containers_containers")
      .select(`
        container_id,
        containers(
          id,
          reference,
          status,
          eta,
          eta_customer,
          etd,
          delayed_etd,
          track_and_trace
        )
      `)
      .eq("master_container_id", masterContainerId);

    if (containerError) throw containerError;

    const containers = containerData?.map(rel => rel.containers).filter(Boolean).flat() || [];
    const containerIds = containers.map(c => c.id);

    // Get bookings in these containers
    const { data: bookingData, error: bookingError } = await supabase
      .from("bookings")
      .select("id, reference, status, container_id")
      .in("container_id", containerIds);

    if (bookingError) throw bookingError;

    return {
      containers: containers,
      bookings: bookingData || [],
      error: null
    };
  } catch (error) {
    console.error("Error getting update preview:", error);
    return { containers: [], bookings: [], error };
  }
};

/**
 * Delete a master container and all its relationships
 */
export const deleteMasterContainer = async (id: number) => {
  try {
    // First delete all container relationships
    const { error: relationError } = await supabase
      .from("master_containers_containers")
      .delete()
      .eq("master_container_id", id);

    if (relationError) throw relationError;

    // Then delete the master container
    const { error } = await supabase
      .from("master_containers")
      .delete()
      .eq("id", id);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error("Error deleting master container:", error);
    return { success: false, error };
  }
};

/**
 * Add a container to a master container.
 * Rejects when master transport_type does not match container transport_type.
 */
export const addContainerToMaster = async (masterId: number, containerId: number) => {
  try {
    const [{ data: master, error: masterError }, { data: container, error: containerError }] =
      await Promise.all([
        supabase
          .from("master_containers")
          .select(
            "id, transport_type, origin_port, destination_port, transport_type_info:booking_transport_types(name)"
          )
          .eq("id", masterId)
          .maybeSingle(),
        supabase
          .from("containers")
          .select("id, transport_type, origin_port, destination_port")
          .eq("id", containerId)
          .maybeSingle(),
      ]);

    if (masterError) throw masterError;
    if (containerError) throw containerError;
    if (!master) {
      return { success: false, data: null, error: new Error("Master BL/AWB not found.") };
    }
    if (!container) {
      return { success: false, data: null, error: new Error("Container not found.") };
    }

    if (master.transport_type != null) {
      const matches = masterTransportTypeMatches(
        master.transport_type,
        container.transport_type
      );
      if (!matches) {
        const info = master.transport_type_info as
          | { name?: string }
          | { name?: string }[]
          | null
          | undefined;
        const masterLabel = Array.isArray(info)
          ? info[0]?.name
          : info?.name;
        return {
          success: false,
          data: null,
          error: new Error(
            `Container transport type does not match this Master BL/AWB${
              masterLabel ? ` (${masterLabel})` : ""
            }.`
          ),
        };
      }
    }

    // Check if the relationship already exists
    const { data: existing, error: checkError } = await supabase
      .from("master_containers_containers")
      .select("id")
      .eq("master_container_id", masterId)
      .eq("container_id", containerId)
      .single();

    if (checkError && checkError.code !== "PGRST116") {
      throw checkError;
    }

    if (existing) {
      return { success: false, error: new Error("Container is already linked to this bill of lading (B/L).") };
    }

    const { data, error } = await supabase
      .from("master_containers_containers")
      .insert({
        master_container_id: masterId,
        container_id: containerId,
      })
      .select()
      .single();

    if (error) throw error;

    // Prefill empty container ports from the B/L (do not overwrite existing values).
    const portPatch: {
      origin_port?: number;
      destination_port?: number;
    } = {};
    if (container.origin_port == null && master.origin_port != null) {
      portPatch.origin_port = master.origin_port;
    }
    if (container.destination_port == null && master.destination_port != null) {
      portPatch.destination_port = master.destination_port;
    }
    if (Object.keys(portPatch).length > 0) {
      const { error: portError } = await supabase
        .from("containers")
        .update(portPatch)
        .eq("id", containerId);
      if (portError) throw portError;
    }

    return { success: true, data, error: null };
  } catch (error) {
    console.error("Error adding container to master:", error);
    return { success: false, data: null, error };
  }
};

/**
 * Remove a container from a master container
 */
export const removeContainerFromMaster = async (masterId: number, containerId: number) => {
  try {
    const { error } = await supabase
      .from("master_containers_containers")
      .delete()
      .eq("master_container_id", masterId)
      .eq("container_id", containerId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error("Error removing container from master:", error);
    return { success: false, error };
  }
};

/**
 * Get available containers (not assigned to any master container).
 * Optionally filter by master transport_type id.
 */
export const getAvailableContainers = async (options?: {
  transportTypeId?: number | null;
}) => {
  try {
    // First get all container IDs that are already assigned to master containers
    const { data: assignedContainerIds, error: assignedError } = await supabase
      .from("master_containers_containers")
      .select("container_id")
      .not("container_id", "is", null);

    if (assignedError) throw assignedError;

    // Extract the IDs into an array
    const assignedIds = assignedContainerIds?.map(item => item.container_id) || [];

    // Now get containers that are not in the assigned list
    let query = supabase
      .from("containers")
      .select(`
        id,
        reference,
        status,
        etd,
        eta,
        track_and_trace,
        transport_type,
        transport_type_info:booking_transport_types(name, abbreviation)
      `);

    // Only add the not in filter if there are assigned containers
    if (assignedIds.length > 0) {
      query = query.not("id", "in", `(${assignedIds.join(",")})`);
    }

    if (options?.transportTypeId != null) {
      query = query.eq("transport_type", options.transportTypeId);
    }

    const { data, error } = await query.order("created_at", { ascending: false });

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error("Error fetching available containers:", error);
    return { data: [], error };
  }
};

/**
 * Get containers that belong to a specific master container
 */
export const getContainersByMasterContainer = async (masterContainerId: number) => {
  try {
    const { data, error } = await supabase
      .from("master_containers_containers")
      .select("container_id")
      .eq("master_container_id", masterContainerId);

    if (error) throw error;

    const containerIds = data?.map(item => item.container_id).filter(Boolean) || [];
    return { data: containerIds, error: null };
  } catch (error) {
    console.error("Error fetching containers by master container:", error);
    return { data: [], error };
  }
}; 